// features/procurement/CreateGRNPage.tsx
/**
 * Create GRN (Goods Received Note) — Redesigned with high-tier SaaS UI/UX.
 * Features:
 * - 1-Click PO synchronization (loads ordered quantities, balances, and vendor specifics)
 * - Support for direct receipts without prior PO
 * - Quality Inspection controls (Quantity Received vs Rejected with predefined reason tags)
 * - Batch number and Expiry Date tracking per line item
 * - Real-time stock valuation preview
 * - Zero silent failures with pre-validation and descriptive notifications
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Spinner } from '@/components/ui/Spinner';
import { getBranches } from '@/features/settings/services/settingsApi';
import apiClient from '@/lib/api-client';
import {
    ArrowLeft, Plus, Save, Trash2, Truck, Store,
    Search, CheckCircle2, AlertCircle, Info, Calendar,
    ChevronRight, ChevronLeft, Package, Sparkles,
    ShieldAlert, Hash, FileCheck, X
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import {
    getVendors,
    getPurchaseOrders,
    createGRN,
    getProcurementErrorMessage,
    Vendor,
    PurchaseOrder,
} from './services/procurementApi';
import { QuickBarcodeScannerInput } from '@/components/ui/QuickBarcodeScannerInput';

// ============================================================
// TYPES
// ============================================================

interface GRNItem {
    id: string; // local temporary key
    poItemId?: string;
    productId: string;
    productName: string;
    sku?: string;
    barcode?: string;
    quantityOrdered?: string;
    alreadyReceived?: string;
    quantityReceived: string;
    quantityRejected: string;
    uomId: string;
    uomName?: string;
    availableUoms: Array<{ id: string; name: string; isBaseUom?: boolean }>;
    unitCost: string;
    sellingPrice?: string;
    batchNumber: string;
    expiryDate: string;
    rejectionReason: string;
}

const REJECTION_REASONS = [
    'Damaged in transit',
    'Substandard quality',
    'Near expiry / Expired',
    'Incorrect item delivered',
    'Broken seals / Compromised packaging',
    'Failed lab inspection',
    'Over-shipped quantity',
];

export function CreateGRNPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const urlPoId = searchParams.get('poId');
    const user = useAuthStore((s) => s.user);
    const selectedBranchId = useAuthStore((s) => s.selectedBranchId);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    // Permission check for updating retail prices in catalog
    const canEditPrices = Boolean(
        (user as any)?.role === 'Tenant Admin' ||
        (user as any)?.permissions?.includes('products:edit') ||
        (user as any)?.permissions?.includes('*') ||
        (user as any)?.isSuperAdmin ||
        (user as any)?.isPlatformAdmin ||
        user?.posAccess !== 'retail' // Retail-only staff (cashiers) are restricted
    );
    const [updateCatalogPrices, setUpdateCatalogPrices] = useState(true);

    const [currentStep, setCurrentStep] = useState<1 | 2>(1);
    const [form, setForm] = useState({
        branchId: selectedBranchId || '',
        poId: urlPoId || '',
        vendorId: '',
        deliveryNoteNumber: '',
        invoiceNumber: '',
        notes: '',
    });

    const [items, setItems] = useState<GRNItem[]>([
        {
            id: 'item-1',
            productId: '',
            productName: '',
            quantityReceived: '1',
            quantityRejected: '0',
            uomId: '',
            availableUoms: [],
            unitCost: '',
            sellingPrice: '',
            batchNumber: '',
            expiryDate: '',
            rejectionReason: '',
        },
    ]);

    const [activeProductDropdownIndex, setActiveProductDropdownIndex] = useState<number | null>(null);
    const [productSearch, setProductSearch] = useState('');
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    // ============================================================
    // QUERIES
    // ============================================================

    const { data: branchesData, isLoading: isLoadingBranches } = useQuery({
        queryKey: ['branches-grn', user?.tenantId],
        queryFn: () => getBranches(user!.tenantId),
        enabled: !!user?.tenantId,
    });

    const { data: vendorsData, isLoading: isLoadingVendors } = useQuery<Vendor[]>({
        queryKey: ['vendors-grn', user?.tenantId],
        queryFn: () => getVendors(user!.tenantId, { limit: 100 }),
        enabled: !!user?.tenantId,
    });

    const { data: purchaseOrdersData, isLoading: isLoadingPOs } = useQuery<PurchaseOrder[]>({
        queryKey: ['pos-grn', user?.tenantId],
        queryFn: async () => {
            const result = await getPurchaseOrders(user!.tenantId, { limit: 100 });
            return (result.pos || []).filter((po) =>
                po.status === 'approved' || po.status === 'sent' || po.status === 'partially_received'
            );
        },
        enabled: !!user?.tenantId,
    });

    const { data: productsData } = useQuery({
        queryKey: ['products-grn', user?.tenantId, productSearch],
        queryFn: async () => {
            const params: any = { limit: 50 };
            if (productSearch.trim()) params.search = productSearch.trim();
            const { data } = await apiClient.get(`/tenants/${user!.tenantId}/products`, { params });
            if (Array.isArray(data)) return data;
            if (data?.data && Array.isArray(data.data)) return data.data;
            return [];
        },
        enabled: !!user?.tenantId,
    });

    const branches = branchesData || [];
    const vendors = vendorsData || [];
    const purchaseOrders = purchaseOrdersData || [];
    const products = productsData || [];
    const selectedPO = purchaseOrders.find((po) => po.id === form.poId);

    // ============================================================
    // PO AUTO-FILL EFFECT
    // ============================================================

    const handlePOSelect = (poId: string) => {
        const po = purchaseOrders.find((p) => p.id === poId);
        if (po) {
            setForm((prev) => ({
                ...prev,
                poId,
                vendorId: po.vendorId,
                branchId: po.branchId || prev.branchId,
                notes: po.notes ? `Linked to PO #${po.poNumber}. ${po.notes}` : `Linked to PO #${po.poNumber}`,
            }));

            if (po.items && po.items.length > 0) {
                const mappedItems: GRNItem[] = po.items.map((pi: any, idx: number) => {
                    const ordered = parseFloat(pi.quantityOrdered || '0');
                    const alreadyRecv = parseFloat(pi.quantityReceived || '0');
                    const remaining = Math.max(0, ordered - alreadyRecv);

                    return {
                        id: `po-item-${pi.id || idx}`,
                        poItemId: pi.id,
                        productId: pi.productId,
                        productName: pi.productName || 'Inventory Product',
                        sku: pi.sku,
                        quantityOrdered: String(ordered),
                        alreadyReceived: String(alreadyRecv),
                        quantityReceived: remaining > 0 ? String(remaining) : '0',
                        quantityRejected: '0',
                        uomId: pi.uomId,
                        uomName: pi.uomName || 'Unit',
                        availableUoms: [{ id: pi.uomId, name: pi.uomName || 'Unit', isBaseUom: true }],
                        unitCost: String(pi.unitCost || '0.00'),
                        batchNumber: '',
                        expiryDate: '',
                        rejectionReason: '',
                    };
                });
                setItems(mappedItems);
            }
        } else {
            setForm((prev) => ({ ...prev, poId }));
        }
    };

    useEffect(() => {
        if (urlPoId && purchaseOrders.length > 0 && form.poId !== urlPoId) {
            handlePOSelect(urlPoId);
        }
    }, [urlPoId, purchaseOrders]);

    // ============================================================
    // MUTATION
    // ============================================================

    const createGRNMutation = useMutation({
        mutationFn: async () => {
            const errors: string[] = [];

            if (!form.branchId) errors.push('Please select a receiving Branch facility.');
            if (!form.vendorId) errors.push('Please select a Supplier/Vendor.');

            const validItems = items.filter(i => i.productId && parseFloat(i.quantityReceived || '0') > 0);
            if (validItems.length === 0) {
                errors.push('At least one line item with valid product, quantity received, and cost is required.');
            }

            items.forEach((item, idx) => {
                if (!item.productId) {
                    errors.push(`Item #${idx + 1}: No product selected.`);
                } else {
                    const received = parseFloat(item.quantityReceived || '0');
                    const rejected = parseFloat(item.quantityRejected || '0');

                    if (received <= 0) {
                        errors.push(`Item #${idx + 1} (${item.productName || 'Product'}): Quantity received must be greater than 0.`);
                    }
                    if (rejected > received) {
                        errors.push(`Item #${idx + 1} (${item.productName || 'Product'}): Quantity rejected (${rejected}) cannot exceed quantity received (${received}).`);
                    }
                    if (rejected > 0 && !item.rejectionReason?.trim()) {
                        errors.push(`Item #${idx + 1} (${item.productName || 'Product'}): Please specify a rejection reason for the ${rejected} rejected unit(s).`);
                    }
                    if (!item.uomId) {
                        errors.push(`Item #${idx + 1} (${item.productName || 'Product'}): Unit of Measure (UOM) is required.`);
                    }
                }
            });

            if (errors.length > 0) {
                setValidationErrors(errors);
                throw new Error(errors[0]);
            }

            setValidationErrors([]);

            return createGRN(user!.tenantId, {
                branchId: form.branchId,
                poId: form.poId || undefined,
                vendorId: form.vendorId,
                deliveryNoteNumber: form.deliveryNoteNumber?.trim() || undefined,
                invoiceNumber: form.invoiceNumber?.trim() || undefined,
                notes: form.notes?.trim() || undefined,
                updateSellingPrices: canEditPrices && updateCatalogPrices,
                items: validItems.map(i => ({
                    poItemId: i.poItemId || undefined,
                    productId: i.productId,
                    quantityOrdered: i.quantityOrdered || undefined,
                    quantityReceived: i.quantityReceived,
                    quantityRejected: i.quantityRejected || '0',
                    uomId: i.uomId,
                    unitCost: i.unitCost,
                    sellingPrice: i.sellingPrice ? String(i.sellingPrice) : undefined,
                    batchNumber: i.batchNumber?.trim() || undefined,
                    expiryDate: i.expiryDate ? new Date(i.expiryDate).toISOString() : undefined,
                    rejectionReason: i.rejectionReason?.trim() || undefined,
                })),
            });
        },
        onSuccess: (grn) => {
            addToast({
                type: 'success',
                title: 'Goods Receipt Registered',
                message: `GRN #${grn.grnNumber} has been logged. Review and confirm to post stock.`,
            });
            queryClient.invalidateQueries({ queryKey: ['grns-list'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-grns'] });
            queryClient.invalidateQueries({ queryKey: ['purchase-orders-list'] });
            navigate(`/procurement/grns/${grn.id}`);
        },
        onError: (err: any) => {
            const message = getProcurementErrorMessage(err, err?.message || 'Failed to create Goods Received Note.');
            addToast({
                type: 'error',
                title: 'Receipt Ingestion Error',
                message,
            });
        },
    });

    // ============================================================
    // HANDLERS
    // ============================================================

    const addItem = () => {
        setItems(prev => [
            ...prev,
            {
                id: `grn-item-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
                productId: '',
                productName: '',
                quantityReceived: '1',
                quantityRejected: '0',
                uomId: '',
                availableUoms: [],
                unitCost: '',
                sellingPrice: '',
                batchNumber: '',
                expiryDate: '',
                rejectionReason: '',
            },
        ]);
    };

    const removeItem = (idx: number) => {
        if (items.length <= 1) {
            addToast({
                type: 'warning',
                title: 'Action Restricted',
                message: 'At least one item row must remain.',
            });
            return;
        }
        setItems(prev => prev.filter((_, i) => i !== idx));
    };

    const updateItem = (idx: number, field: keyof GRNItem, value: any) => {
        setItems(prev => {
            const updated = [...prev];
            updated[idx] = { ...updated[idx], [field]: value };
            return updated;
        });
    };

    const selectProduct = (idx: number, product: any) => {
        const uoms = Array.isArray(product.uoms) && product.uoms.length > 0
            ? product.uoms
            : [{ id: product.uomId || '00000000-0000-0000-0000-000000000000', name: product.uomName || 'Unit', isBaseUom: true }];

        const defaultUom = uoms.find((u: any) => u.isBaseUom) || uoms[0];

        setItems(prev => {
            const updated = [...prev];
            updated[idx] = {
                ...updated[idx],
                productId: product.id,
                productName: product.name,
                sku: product.sku || '',
                barcode: product.barcode || '',
                availableUoms: uoms,
                uomId: defaultUom?.id || '',
                uomName: defaultUom?.name || 'Unit',
                unitCost: product.costPrice ? String(product.costPrice) : updated[idx].unitCost || '0.00',
                sellingPrice: product.retailPrice ? String(product.retailPrice) : '',
            };
            return updated;
        });
        setActiveProductDropdownIndex(null);
    };

    const handleBarcodeScan = (scannedCode: string) => {
        const code = scannedCode.trim().toLowerCase();
        // Check if already in items list
        const existingIdx = items.findIndex(
            (it) => it.sku?.toLowerCase() === code || it.barcode?.toLowerCase() === code
        );

        if (existingIdx !== -1) {
            const currentQty = parseFloat(items[existingIdx].quantityReceived || '0');
            updateItem(existingIdx, 'quantityReceived', String(currentQty + 1));
            addToast({
                type: 'success',
                title: 'Quantity Incremented',
                message: `Scanned "${items[existingIdx].productName}" (+1 unit)`,
            });
            return;
        }

        // Look up in products list
        const match = products.find(
            (p: any) =>
                p.barcode?.toLowerCase() === code ||
                p.sku?.toLowerCase() === code
        );

        if (match) {
            const firstEmptyIdx = items.findIndex((it) => !it.productId);
            if (firstEmptyIdx !== -1) {
                selectProduct(firstEmptyIdx, match);
            } else {
                const uoms = Array.isArray(match.uoms) && match.uoms.length > 0
                    ? match.uoms
                    : [{ id: match.uomId || '00000000-0000-0000-0000-000000000000', name: match.uomName || 'Unit', isBaseUom: true }];
                const defaultUom = uoms.find((u: any) => u.isBaseUom) || uoms[0];

                setItems((prev) => [
                    ...prev,
                    {
                        id: `grn-item-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
                        productId: match.id,
                        productName: match.name,
                        sku: match.sku || '',
                        barcode: match.barcode || '',
                        quantityReceived: '1',
                        quantityRejected: '0',
                        uomId: defaultUom?.id || '',
                        uomName: defaultUom?.name || 'Unit',
                        availableUoms: uoms,
                        unitCost: match.costPrice ? String(match.costPrice) : '0.00',
                        sellingPrice: match.retailPrice ? String(match.retailPrice) : '',
                        batchNumber: '',
                        expiryDate: '',
                        rejectionReason: '',
                    },
                ]);
            }
            addToast({
                type: 'success',
                title: 'Product Added via Barcode',
                message: `Scanned "${match.name}"`,
            });
        } else {
            addToast({
                type: 'warning',
                title: 'Barcode Not Found in Catalog',
                message: `No product found matching code "${scannedCode}"`,
            });
        }
    };

    // ============================================================
    // METRICS
    // ============================================================

    const totalReceivedCount = items.reduce((sum, item) => sum + parseFloat(item.quantityReceived || '0'), 0);
    const totalRejectedCount = items.reduce((sum, item) => sum + parseFloat(item.quantityRejected || '0'), 0);
    const totalAcceptedCount = Math.max(0, totalReceivedCount - totalRejectedCount);

    const totalReceiptValue = items.reduce((sum, item) => {
        const accepted = Math.max(0, parseFloat(item.quantityReceived || '0') - parseFloat(item.quantityRejected || '0'));
        const cost = parseFloat(item.unitCost || '0');
        return sum + (accepted * (isNaN(cost) ? 0 : cost));
    }, 0);

    const formatNaira = (val: number): string =>
        `₦${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const canProceedToItems = Boolean(form.branchId && form.vendorId);
    const validItemsCount = items.filter(
        i => i.productId && parseFloat(i.quantityReceived || '0') > 0 && i.uomId
    ).length;
    const isReadyToSubmit = canProceedToItems && validItemsCount > 0;

    return (
        <div className="min-h-screen bg-slate-50/60 text-slate-800 pb-20">
            {/* Top Action Bar */}
            <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate('/procurement/grns')}
                                className="p-2.5 rounded-xl bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 transition-all shadow-sm"
                                type="button"
                                title="Back to GRN List"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-teal-600 to-emerald-600 rounded-2xl shadow-md shadow-teal-500/20 text-white">
                                <Truck className="w-6 h-6" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h1 className="text-xl sm:text-2xl font-black tracking-tight text-slate-900">
                                        Goods Received Note (GRN)
                                    </h1>
                                    <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-teal-50 text-teal-700 border border-teal-200 uppercase tracking-wider">
                                        Inbound Dock
                                    </span>
                                </div>
                                <p className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                                    <span>{selectedPO ? `Against PO #${selectedPO.poNumber}` : 'Direct Warehouse Receiving'}</span>
                                    <span>•</span>
                                    <span>{totalAcceptedCount.toLocaleString()} accepted units</span>
                                </p>
                            </div>
                        </div>

                        {/* Top CTAs */}
                        <div className="flex items-center gap-3 self-end sm:self-auto">
                            <button
                                onClick={() => navigate('/procurement/grns')}
                                className="px-4 py-2.5 rounded-xl border border-slate-200 hover:bg-slate-100 text-slate-700 font-semibold text-sm transition-all"
                                type="button"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => createGRNMutation.mutate()}
                                disabled={!isReadyToSubmit || createGRNMutation.isPending}
                                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl hover:from-teal-700 hover:to-emerald-700 transition-all font-bold text-sm shadow-md shadow-teal-500/25 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                                type="button"
                            >
                                {createGRNMutation.isPending ? (
                                    <>
                                        <Spinner size="xs" variant="white" />
                                        <span>Logging Receipt...</span>
                                    </>
                                ) : (
                                    <>
                                        <FileCheck className="w-4 h-4" />
                                        <span>Record Inbound Goods</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 space-y-8">
                {/* Step Indicators */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button
                        type="button"
                        onClick={() => setCurrentStep(1)}
                        className={clsx(
                            'p-4 rounded-2xl border text-left transition-all flex items-center justify-between',
                            currentStep === 1
                                ? 'bg-white border-2 border-teal-600 text-teal-900 shadow-sm'
                                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300'
                        )}
                    >
                        <div className="flex items-center gap-3">
                            <div className={clsx(
                                'w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm',
                                canProceedToItems ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-600'
                            )}>
                                {canProceedToItems ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : '1'}
                            </div>
                            <div>
                                <p className="text-sm font-bold text-slate-900">Step 1: Receipt Header & Reference</p>
                                <p className="text-xs text-slate-500">PO linkage, supplier delivery note & destination branch</p>
                            </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                    </button>

                    <button
                        type="button"
                        onClick={() => {
                            if (canProceedToItems) setCurrentStep(2);
                            else {
                                addToast({
                                    type: 'warning',
                                    title: 'Required Details',
                                    message: 'Please set the receiving facility and supplier before receiving items.',
                                });
                            }
                        }}
                        className={clsx(
                            'p-4 rounded-2xl border text-left transition-all flex items-center justify-between',
                            currentStep === 2
                                ? 'bg-white border-2 border-teal-600 text-teal-900 shadow-sm'
                                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300'
                        )}
                    >
                        <div className="flex items-center gap-3">
                            <div className={clsx(
                                'w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm',
                                validItemsCount > 0 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-600'
                            )}>
                                {validItemsCount > 0 ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : '2'}
                            </div>
                            <div>
                                <p className="text-sm font-bold text-slate-900">Step 2: Physical Inspection & Batches</p>
                                <p className="text-xs text-slate-500">{validItemsCount} items verified · Valuation: {formatNaira(totalReceiptValue)}</p>
                            </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                    </button>
                </div>

                {/* Validation Banner if errors exist */}
                {validationErrors.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-sm flex items-start gap-3"
                    >
                        <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                        <div className="space-y-1">
                            <p className="font-bold text-rose-900">Unable to log Goods Received Note due to errors:</p>
                            <ul className="list-disc list-inside text-xs space-y-0.5 text-rose-700">
                                {validationErrors.map((err, i) => (
                                    <li key={i}>{err}</li>
                                ))}
                            </ul>
                        </div>
                        <button
                            onClick={() => setValidationErrors([])}
                            className="ml-auto text-rose-500 hover:text-rose-700 p-1"
                            type="button"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </motion.div>
                )}

                {/* STEP 1: RECEIPT HEADERS */}
                {currentStep === 1 && (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        className="space-y-6"
                    >
                        <div className="bg-white border border-slate-200 rounded-3xl p-6 lg:p-8 shadow-sm space-y-6">
                            <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                                <Truck className="w-5 h-5 text-teal-600" />
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900">Inbound Delivery Information</h3>
                                    <p className="text-xs text-slate-500">Match against an active purchase order or register a direct delivery from supplier</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* Optional Purchase Order Link */}
                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                                        Linked Purchase Order (Optional)
                                    </label>
                                    <select
                                        value={form.poId}
                                        onChange={(e) => handlePOSelect(e.target.value)}
                                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
                                    >
                                        <option value="">Direct Receiving (No PO)</option>
                                        {purchaseOrders.map((po) => (
                                            <option key={po.id} value={po.id}>
                                                {po.poNumber} — {po.vendorName} [{po.status}]
                                            </option>
                                        ))}
                                    </select>
                                    {purchaseOrders.length === 0 && !isLoadingPOs && (
                                        <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1">
                                            <Info className="w-3.5 h-3.5" /> No approved POs awaiting goods receipt.
                                        </p>
                                    )}
                                </div>

                                {/* Destination Branch */}
                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                                        Receiving Branch <span className="text-teal-600">*</span>
                                    </label>
                                    <select
                                        value={form.branchId}
                                        onChange={(e) => setForm({ ...form, branchId: e.target.value })}
                                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
                                        required
                                    >
                                        <option value="">Select facility...</option>
                                        {branches.map((b: any) => (
                                            <option key={b.id} value={b.id}>
                                                {b.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Vendor */}
                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                                        Supplier / Vendor <span className="text-teal-600">*</span>
                                    </label>
                                    <select
                                        value={form.vendorId}
                                        onChange={(e) => setForm({ ...form, vendorId: e.target.value })}
                                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
                                        required
                                    >
                                        <option value="">Select supplier...</option>
                                        {vendors.map((v: Vendor) => (
                                            <option key={v.id} value={v.id}>
                                                {v.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Reference Documents */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                                        Waybill / Delivery Note Number
                                    </label>
                                    <input
                                        type="text"
                                        value={form.deliveryNoteNumber}
                                        onChange={(e) => setForm({ ...form, deliveryNoteNumber: e.target.value })}
                                        placeholder="e.g. WB-2026-9041"
                                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all placeholder:text-slate-400"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                                        Supplier Invoice Number
                                    </label>
                                    <input
                                        type="text"
                                        value={form.invoiceNumber}
                                        onChange={(e) => setForm({ ...form, invoiceNumber: e.target.value })}
                                        placeholder="e.g. INV-88219"
                                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all placeholder:text-slate-400"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                                    Receiving Inspection Notes (Optional)
                                </label>
                                <textarea
                                    value={form.notes}
                                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    rows={2}
                                    placeholder="Add any dock remarks, condition of truck/container, driver identification..."
                                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 resize-none focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all placeholder:text-slate-400"
                                />
                            </div>

                            {/* Step 1 Footer */}
                            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                                <span className="text-xs text-slate-500">
                                    {canProceedToItems ? (
                                        <span className="text-emerald-600 flex items-center gap-1 font-medium">
                                            <CheckCircle2 className="w-4 h-4" /> Ready to inspect goods at dock.
                                        </span>
                                    ) : (
                                        'Select destination Branch and Supplier to proceed.'
                                    )}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep(2)}
                                    disabled={!canProceedToItems}
                                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white rounded-xl font-bold text-sm shadow-md shadow-teal-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                >
                                    <span>Proceed to Quality Inspection</span>
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* STEP 2: QUALITY INSPECTION & BATCHES */}
                {currentStep === 2 && (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        className="space-y-6"
                    >
                        <div className="bg-white border border-slate-200 rounded-3xl p-6 lg:p-8 shadow-sm space-y-6">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-slate-100">
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                        <Package className="w-5 h-5 text-teal-600" />
                                        <span>Inspection & Acceptance Grid</span>
                                    </h3>
                                    <p className="text-xs text-slate-500">
                                        Verify physical quantity received, segregate damaged or rejected units, and capture batch/expiry data
                                    </p>
                                </div>
                                <button
                                    onClick={addItem}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-teal-50 hover:bg-teal-100 border border-teal-200 text-teal-700 rounded-xl font-bold text-xs transition-all shadow-sm self-start sm:self-auto cursor-pointer"
                                    type="button"
                                >
                                    <Plus className="w-4 h-4" />
                                    <span>Add Item Row</span>
                                </button>
                            </div>

                            {/* Quick Barcode Scanner & Price Update Toggle Banner */}
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 p-4 bg-slate-50 border border-slate-200 rounded-2xl">
                                <div className="flex-1 max-w-md">
                                    <QuickBarcodeScannerInput
                                        size="sm"
                                        placeholder="Scan barcode to locate or increment line item..."
                                        onScan={handleBarcodeScan}
                                    />
                                </div>

                                {/* Toggle: Update catalog retail prices upon confirmation */}
                                <div className="flex items-center gap-3">
                                    <label className={clsx(
                                        "flex items-center gap-2.5 text-xs font-semibold cursor-pointer select-none px-3 py-2 rounded-xl border transition-colors",
                                        canEditPrices
                                            ? updateCatalogPrices
                                                ? "bg-teal-50 border-teal-200 text-teal-800"
                                                : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                                            : "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed"
                                    )}>
                                        <input
                                            type="checkbox"
                                            checked={canEditPrices && updateCatalogPrices}
                                            disabled={!canEditPrices}
                                            onChange={(e) => setUpdateCatalogPrices(e.target.checked)}
                                            className="w-4 h-4 rounded text-teal-600 focus:ring-teal-500 border-slate-300 disabled:opacity-50"
                                        />
                                        <div className="flex flex-col">
                                            <span>Update catalog retail prices upon confirmation</span>
                                            {!canEditPrices && (
                                                <span className="text-[10px] text-slate-400 font-normal">
                                                    Requires products:edit permission
                                                </span>
                                            )}
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/* Inspection Items */}
                            <div className="space-y-4">
                                {items.map((item, idx) => {
                                    const received = parseFloat(item.quantityReceived || '0');
                                    const rejected = parseFloat(item.quantityRejected || '0');
                                    const accepted = Math.max(0, received - rejected);
                                    const lineCost = parseFloat(item.unitCost || '0');
                                    const lineAcceptedValue = accepted * (isNaN(lineCost) ? 0 : lineCost);
                                    const selling = parseFloat(item.sellingPrice || '0');
                                    const margin = selling > 0 && lineCost > 0 ? ((selling - lineCost) / selling) * 100 : null;

                                    return (
                                        <div
                                            key={item.id}
                                            className={clsx(
                                                'p-5 rounded-2xl border transition-all space-y-4',
                                                rejected > 0
                                                    ? 'bg-rose-50/50 border-rose-200'
                                                    : 'bg-slate-50/70 border-slate-200 hover:border-teal-500/40'
                                            )}
                                        >
                                            <div className="flex items-center justify-between text-xs">
                                                <div className="flex items-center gap-3">
                                                    <span className="font-bold uppercase tracking-wider text-teal-700">
                                                        Item #{idx + 1}
                                                    </span>
                                                    {item.quantityOrdered && (
                                                        <span className="px-2 py-0.5 rounded-md bg-white text-slate-700 border border-slate-200 text-[10px]">
                                                            PO Ordered: {parseFloat(item.quantityOrdered).toLocaleString()} {item.uomName || ''}
                                                        </span>
                                                    )}
                                                    {item.alreadyReceived && parseFloat(item.alreadyReceived) > 0 && (
                                                        <span className="px-2 py-0.5 rounded-md bg-teal-50 text-teal-700 border border-teal-200 text-[10px]">
                                                            Already Recv: {parseFloat(item.alreadyReceived).toLocaleString()}
                                                        </span>
                                                    )}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeItem(idx)}
                                                    className="text-slate-400 hover:text-rose-600 transition-colors p-1"
                                                    title="Remove row"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>

                                            {/* Primary Columns */}
                                            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                                                {/* Product Selector */}
                                                <div className="md:col-span-3 relative">
                                                    <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                                                        Product Catalog *
                                                    </label>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setActiveProductDropdownIndex(activeProductDropdownIndex === idx ? null : idx);
                                                            setProductSearch('');
                                                        }}
                                                        className="w-full px-3 py-2 bg-white border border-slate-200 hover:border-teal-400 rounded-xl text-left text-sm text-slate-800 flex items-center justify-between transition-all"
                                                    >
                                                        {item.productId ? (
                                                            <div className="truncate">
                                                                <p className="font-bold text-slate-900 text-xs truncate">{item.productName}</p>
                                                                {item.sku && <p className="text-[10px] text-slate-500 font-mono">SKU: {item.sku}</p>}
                                                            </div>
                                                        ) : (
                                                            <span className="text-slate-400 text-xs flex items-center gap-2">
                                                                <Search className="w-3.5 h-3.5" /> Select catalog product...
                                                            </span>
                                                        )}
                                                        <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                                                    </button>

                                                    <AnimatePresence>
                                                        {activeProductDropdownIndex === idx && (
                                                            <motion.div
                                                                initial={{ opacity: 0, y: -4 }}
                                                                animate={{ opacity: 1, y: 0 }}
                                                                exit={{ opacity: 0, y: -4 }}
                                                                className="absolute top-full mt-2 w-full md:w-96 bg-white border border-slate-200 rounded-2xl shadow-2xl z-50 overflow-hidden left-0"
                                                            >
                                                                <div className="p-3 border-b border-slate-100 bg-slate-50">
                                                                    <div className="relative">
                                                                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                                                        <input
                                                                            type="text"
                                                                            placeholder="Search product name or SKU..."
                                                                            value={productSearch}
                                                                            onChange={(e) => setProductSearch(e.target.value)}
                                                                            className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:border-teal-500"
                                                                            autoFocus
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="max-h-60 overflow-y-auto divide-y divide-slate-100 p-1">
                                                                    {products.length === 0 ? (
                                                                        <div className="p-4 text-center text-xs text-slate-500">
                                                                            No matching products found.
                                                                        </div>
                                                                    ) : (
                                                                        products.map((p: any) => (
                                                                            <button
                                                                                key={p.id}
                                                                                type="button"
                                                                                onClick={() => selectProduct(idx, p)}
                                                                                className={clsx(
                                                                                    'w-full text-left p-3 rounded-xl transition-all hover:bg-slate-50 flex items-center justify-between',
                                                                                    item.productId === p.id && 'bg-teal-50 border border-teal-200'
                                                                                )}
                                                                            >
                                                                                <div className="truncate pr-2">
                                                                                    <p className="text-xs font-bold text-slate-900 truncate">{p.name}</p>
                                                                                    <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500">
                                                                                        {p.sku && <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-700">{p.sku}</span>}
                                                                                        <span>Cost: ₦{parseFloat(p.costPrice || '0').toLocaleString()}</span>
                                                                                        {p.retailPrice && <span>· Sell: ₦{parseFloat(p.retailPrice).toLocaleString()}</span>}
                                                                                    </div>
                                                                                </div>
                                                                                <span className="text-[10px] font-bold text-teal-700 bg-teal-50 px-2 py-1 rounded-lg shrink-0">
                                                                                    Select
                                                                                </span>
                                                                            </button>
                                                                        ))
                                                                    )}
                                                                </div>
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </div>

                                                {/* Quantity Received */}
                                                <div className="md:col-span-2">
                                                    <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                                                        Qty Received *
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="0.001"
                                                        step="any"
                                                        value={item.quantityReceived}
                                                        onChange={(e) => updateItem(idx, 'quantityReceived', e.target.value)}
                                                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-right text-slate-900 focus:outline-none focus:border-teal-500 transition-all"
                                                        required
                                                    />
                                                </div>

                                                {/* Quantity Rejected */}
                                                <div className="md:col-span-1">
                                                    <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                                                        Rejected
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="any"
                                                        value={item.quantityRejected}
                                                        onChange={(e) => updateItem(idx, 'quantityRejected', e.target.value)}
                                                        className={clsx(
                                                            'w-full px-2 py-2 bg-white border rounded-xl text-sm font-semibold text-right transition-all focus:outline-none',
                                                            rejected > 0 ? 'border-rose-400 text-rose-700 bg-rose-50/30' : 'border-slate-200 text-slate-800'
                                                        )}
                                                    />
                                                </div>

                                                {/* Accepted Badge */}
                                                <div className="md:col-span-1 text-center pt-2 md:pt-0">
                                                    <span className="block text-[10px] font-semibold text-slate-400 uppercase">Accepted</span>
                                                    <span className="inline-block mt-1.5 px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 font-bold text-xs border border-emerald-200">
                                                        {accepted.toLocaleString()}
                                                    </span>
                                                </div>

                                                {/* Unit Cost */}
                                                <div className="md:col-span-2">
                                                    <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                                                        Unit Cost (₦) *
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={item.unitCost}
                                                        onChange={(e) => updateItem(idx, 'unitCost', e.target.value)}
                                                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-right text-teal-700 focus:outline-none focus:border-teal-500 transition-all"
                                                        required
                                                    />
                                                </div>

                                                {/* Retail Price (Selling Price) */}
                                                <div className="md:col-span-2">
                                                    <div className="flex items-center justify-between mb-1">
                                                        <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider">
                                                            Selling Price (₦)
                                                        </label>
                                                        {margin !== null && (
                                                            <span className={clsx(
                                                                "text-[9px] font-bold px-1.5 py-0.2 rounded",
                                                                margin >= 20 ? "bg-emerald-100 text-emerald-800" : margin > 0 ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"
                                                            )}>
                                                                {margin.toFixed(1)}% margin
                                                            </span>
                                                        )}
                                                    </div>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        disabled={!canEditPrices}
                                                        value={item.sellingPrice || ''}
                                                        placeholder={canEditPrices ? "0.00" : "Locked"}
                                                        onChange={(e) => updateItem(idx, 'sellingPrice', e.target.value)}
                                                        className={clsx(
                                                            "w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-right transition-all focus:outline-none focus:border-teal-500",
                                                            !canEditPrices && "bg-slate-100 cursor-not-allowed text-slate-400"
                                                        )}
                                                    />
                                                </div>

                                                {/* Accepted Value */}
                                                <div className="md:col-span-1 text-right pt-2 md:pt-0">
                                                    <span className="block text-[10px] font-semibold text-slate-400 uppercase">Valuation</span>
                                                    <p className="text-xs font-bold text-slate-900 mt-1">
                                                        {formatNaira(lineAcceptedValue)}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Batch, Expiry, and Rejection Details Sub-row */}
                                            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 pt-2 border-t border-slate-100">
                                                {/* Batch Number */}
                                                <div className="md:col-span-3">
                                                    <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                                                        Batch / Lot Number
                                                    </label>
                                                    <input
                                                        type="text"
                                                        placeholder="e.g. BATCH-2026-X"
                                                        value={item.batchNumber}
                                                        onChange={(e) => updateItem(idx, 'batchNumber', e.target.value)}
                                                        className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-teal-500 font-mono"
                                                    />
                                                </div>

                                                {/* Expiry Date */}
                                                <div className="md:col-span-3">
                                                    <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                                                        Expiration Date
                                                    </label>
                                                    <input
                                                        type="date"
                                                        value={item.expiryDate}
                                                        onChange={(e) => updateItem(idx, 'expiryDate', e.target.value)}
                                                        className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:border-teal-500"
                                                    />
                                                </div>

                                                {/* Rejection Reason (Mandatory if rejected > 0) */}
                                                <div className="md:col-span-6">
                                                    <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                                                        Rejection Reason {rejected > 0 && <span className="text-rose-600">*</span>}
                                                    </label>
                                                    <div className="flex gap-2">
                                                        <select
                                                            value={item.rejectionReason}
                                                            onChange={(e) => updateItem(idx, 'rejectionReason', e.target.value)}
                                                            className={clsx(
                                                                'w-full px-3 py-1.5 bg-white border rounded-lg text-xs focus:outline-none',
                                                                rejected > 0 && !item.rejectionReason ? 'border-rose-400 text-rose-700 bg-rose-50/40' : 'border-slate-200 text-slate-800'
                                                            )}
                                                        >
                                                            <option value="">Select rejection cause...</option>
                                                            {REJECTION_REASONS.map(r => (
                                                                <option key={r} value={r}>{r}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Summary Metrics Bar */}
                            <div className="pt-6 border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-6">
                                <div className="text-xs text-slate-500 space-y-1 w-full md:w-auto">
                                    <p className="flex items-center gap-2 text-slate-700">
                                        <Sparkles className="w-4 h-4 text-teal-600" />
                                        <span>Warehouse Stock Ingestion Ledger</span>
                                    </p>
                                    <p className="text-[11px] text-slate-400">
                                        Accepted quantities will post into branch warehouse stock upon subsequent GRN confirmation.
                                    </p>
                                </div>

                                <div className="w-full md:w-80 bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-3">
                                    <div className="flex items-center justify-between text-xs text-slate-600">
                                        <span>Total Physical Received</span>
                                        <span className="font-semibold text-slate-900">{totalReceivedCount.toLocaleString()} units</span>
                                    </div>
                                    <div className="flex items-center justify-between text-xs text-slate-600">
                                        <span>Total Damaged / Rejected</span>
                                        <span className={clsx('font-semibold', totalRejectedCount > 0 ? 'text-rose-600' : 'text-slate-500')}>
                                            {totalRejectedCount.toLocaleString()} units
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between text-xs text-slate-600">
                                        <span>Net Accepted Stock</span>
                                        <span className="font-semibold text-emerald-700">{totalAcceptedCount.toLocaleString()} units</span>
                                    </div>
                                    <div className="pt-3 border-t border-slate-200 flex items-center justify-between">
                                        <span className="text-sm font-bold text-slate-900">Accepted Valuation</span>
                                        <span className="text-base font-black text-teal-700">{formatNaira(totalReceiptValue)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Bottom Step Actions */}
                            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep(1)}
                                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 hover:bg-slate-100 text-slate-700 font-semibold text-sm transition-all"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    <span>Back to Header</span>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => createGRNMutation.mutate()}
                                    disabled={!isReadyToSubmit || createGRNMutation.isPending}
                                    className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl hover:from-teal-700 hover:to-emerald-700 transition-all font-bold text-sm shadow-md shadow-teal-500/25 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                                >
                                    {createGRNMutation.isPending ? (
                                        <>
                                            <Spinner size="xs" variant="white" />
                                            <span>Recording Inbound Goods...</span>
                                        </>
                                    ) : (
                                        <>
                                            <FileCheck className="w-4 h-4" />
                                            <span>Record Inbound Goods ({formatNaira(totalReceiptValue)})</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </div>
        </div>
    );
}